/**
 * A Hide and Seek game's round defaults: the settings every new round of the
 * game starts with (Hide and Seek, Hide and Seek Teams). A round follows the
 * defaults while its default settings match them (`usesGameDefaults`); then
 * saving new defaults changes it too, if it has not started. A round whose
 * default settings differ is Custom and keeps them.
 *
 * The location, seekers, hiding team, Team Captain pick, coin toss and the
 * rules page note are each round's own, not defaults.
 */
import { GameType } from '../constants/gameTypes';
import { normalizeHnsRoleRules, DEFAULT_HNS_ROLE_RULES } from './hnsRules';
import { ROUND_EVENT_LEAD_TIME_MIN_SECONDS } from './roundEvent';
import { DEFAULT_HNS_MARCO_POLO_RADIUS, DEFAULT_HNS_SEEKER_VISIBILITY, DEFAULT_HNST_DEATH_OUTCOME, DEFAULT_HNST_ROUND_SCORING, getHnstDeathOutcome, getHnstRoundScoring, resolveHideSeekPublicDisplay, } from './round';
/** The hiding time a round starts with when the game has no defaults. */
export const DEFAULT_HNS_HIDING_DURATION = 30;
/** The round time (after hiding) a round starts with when the game has no defaults. */
export const DEFAULT_HNS_ROUND_DURATION = 600;
export function builtInHideSeekRoundDefaults(gameType) {
    const base = {
        hidingDuration: DEFAULT_HNS_HIDING_DURATION,
        roundDuration: DEFAULT_HNS_ROUND_DURATION,
        hiderRules: { ...DEFAULT_HNS_ROLE_RULES },
        seekerRules: { ...DEFAULT_HNS_ROLE_RULES },
        marcoPoloRadius: DEFAULT_HNS_MARCO_POLO_RADIUS,
        maxCameraDistance: null,
        roundEvents: [],
    };
    return gameType === GameType.HIDE_AND_SEEK_TEAMS
        ? { ...base, gameType, scoring: { ...DEFAULT_HNST_ROUND_SCORING }, deathOutcome: DEFAULT_HNST_DEATH_OUTCOME }
        : { ...base, gameType, seekerVisibility: DEFAULT_HNS_SEEKER_VISIBILITY };
}
/** A game's round defaults: the saved ones, or the built-in ones. */
export function resolveHideSeekRoundDefaults(gameType, saved) {
    return saved?.gameType === gameType ? saved : builtInHideSeekRoundDefaults(gameType);
}
/** A round's scheduled starting events: those set up before it began and not yet fired. */
export function hideSeekInitialRoundEvents(events) {
    return (events ?? [])
        .filter((e) => e.status === 'scheduled' && !e.isRetroactive && e.scheduledOffsetSeconds !== null)
        .map((e) => ({
        kind: e.kind,
        scheduledOffsetSeconds: e.scheduledOffsetSeconds,
        ...(e.leadTimeSeconds !== undefined && { leadTimeSeconds: e.leadTimeSeconds }),
        payload: e.payload,
    }));
}
/** A round's default settings, with the events it starts with. */
export function hideSeekRoundDefaultsOf(settings, roundEvents) {
    const base = {
        hidingDuration: settings.hidingDuration,
        roundDuration: settings.roundDuration,
        hiderRules: settings.hiderRules,
        seekerRules: settings.seekerRules,
        marcoPoloRadius: settings.marcoPoloRadius,
        maxCameraDistance: settings.maxCameraDistance,
        ...(settings.publicDisplay && { publicDisplay: settings.publicDisplay }),
        roundEvents,
    };
    return settings.gameType === GameType.HIDE_AND_SEEK_TEAMS
        ? { ...base, gameType: settings.gameType, scoring: getHnstRoundScoring(settings), deathOutcome: getHnstDeathOutcome(settings) }
        : { ...base, gameType: settings.gameType, seekerVisibility: settings.seekerVisibility };
}
/** A new round's settings: the defaults, with none of the round's own chosen yet. */
export function hideSeekRoundSettingsFromDefaults(defaults) {
    const settings = {
        hidingDuration: defaults.hidingDuration,
        roundDuration: defaults.roundDuration,
        hiderRules: { ...defaults.hiderRules },
        seekerRules: { ...defaults.seekerRules },
        location: null,
        marcoPoloRadius: defaults.marcoPoloRadius,
        maxCameraDistance: defaults.maxCameraDistance,
        ...(defaults.publicDisplay && { publicDisplay: { ...defaults.publicDisplay } }),
    };
    return defaults.gameType === GameType.HIDE_AND_SEEK_TEAMS
        ? { ...settings, gameType: defaults.gameType, hidingTeam: null, ownerTeam: null, scoring: { ...defaults.scoring }, deathOutcome: defaults.deathOutcome }
        : { ...settings, gameType: defaults.gameType, seekerVisibility: defaults.seekerVisibility };
}
/**
 * A round's settings with the defaults put in. Its own settings (location,
 * hiding team, rules page note, …) stay. The events are not settings: the
 * caller gives the round `defaults.roundEvents`.
 */
export function applyHideSeekRoundDefaults(settings, defaults) {
    const applied = {
        ...settings,
        hidingDuration: defaults.hidingDuration,
        roundDuration: defaults.roundDuration,
        hiderRules: { ...defaults.hiderRules },
        seekerRules: { ...defaults.seekerRules },
        marcoPoloRadius: defaults.marcoPoloRadius,
        maxCameraDistance: defaults.maxCameraDistance,
    };
    if (defaults.publicDisplay && Object.keys(defaults.publicDisplay).length > 0) {
        applied.publicDisplay = { ...defaults.publicDisplay };
    }
    else {
        delete applied.publicDisplay;
    }
    if (applied.gameType === GameType.HIDE_AND_SEEK && defaults.gameType === GameType.HIDE_AND_SEEK) {
        applied.seekerVisibility = defaults.seekerVisibility;
    }
    if (applied.gameType === GameType.HIDE_AND_SEEK_TEAMS && defaults.gameType === GameType.HIDE_AND_SEEK_TEAMS) {
        applied.scoring = { ...defaults.scoring };
        applied.deathOutcome = defaults.deathOutcome;
    }
    return applied;
}
/** Whether a round's default settings and starting events are the game's defaults. */
export function matchesHideSeekRoundDefaults(settings, roundEvents, defaults) {
    return canonicalDefaults(hideSeekRoundDefaultsOf(settings, roundEvents)) === canonicalDefaults(defaults);
}
/** Defaults as text that is the same for equal defaults, however they were stored. */
function canonicalDefaults(defaults) {
    return stableJson({
        gameType: defaults.gameType,
        hidingDuration: defaults.hidingDuration,
        roundDuration: defaults.roundDuration,
        hiderRules: normalizeHnsRoleRules(defaults.hiderRules),
        seekerRules: normalizeHnsRoleRules(defaults.seekerRules),
        marcoPoloRadius: defaults.marcoPoloRadius,
        maxCameraDistance: defaults.maxCameraDistance ?? null,
        publicDisplay: resolveHideSeekPublicDisplay(defaults.publicDisplay),
        roundEvents: defaults.roundEvents.map(canonicalEvent).sort(),
        ...(defaults.gameType === GameType.HIDE_AND_SEEK
            ? { seekerVisibility: defaults.seekerVisibility }
            : { scoring: getHnstRoundScoring(defaults), deathOutcome: getHnstDeathOutcome(defaults) }),
    });
}
function canonicalEvent(event) {
    const payload = (event.payload ?? {});
    const isRuleChange = event.kind === 'ruleset_change';
    return stableJson({
        kind: event.kind,
        scheduledOffsetSeconds: event.scheduledOffsetSeconds,
        leadTimeSeconds: isRuleChange ? event.leadTimeSeconds ?? ROUND_EVENT_LEAD_TIME_MIN_SECONDS : null,
        payload: isRuleChange
            ? { hiderRules: normalizeHnsRoleRules(payload.hiderRules), seekerRules: normalizeHnsRoleRules(payload.seekerRules) }
            : payload,
    });
}
/** JSON with object keys sorted (a JSONB column does not keep their order). */
function stableJson(value) {
    if (Array.isArray(value))
        return `[${value.map(stableJson).join(',')}]`;
    if (value && typeof value === 'object') {
        const record = value;
        return `{${Object.keys(record)
            .filter((key) => record[key] !== undefined)
            .sort()
            .map((key) => `${JSON.stringify(key)}:${stableJson(record[key])}`)
            .join(',')}}`;
    }
    return JSON.stringify(value) ?? 'null';
}
